import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, RefreshCw, WifiOff } from 'lucide-react';
import { createConnectionNotice, type ConnectionNotice } from '../lib/connectionNotice';
import { API_CONNECTION_FAILED } from '../lib/apiConnectionMonitor';

export const ConnectionStatusMessage = ({ state, locale }: { state: ConnectionNotice; locale: string }) => {
  if (state === 'hidden') return null;
  const nl = locale === 'nl';
  const restored = state === 'restored';
  const offline = state === 'offline';
  const stopping = state === 'stopping';
  const Icon = restored ? CheckCircle2 : offline ? WifiOff : RefreshCw;
  const title = restored
    ? (nl ? 'Verbinding hersteld' : 'Connection restored')
    : offline
      ? (nl ? 'Je bent offline' : 'You are offline')
      : stopping
        ? (nl ? 'doHockey is tijdelijk niet beschikbaar' : 'doHockey is temporarily unavailable')
        : (nl ? 'Verbinding met doHockey onderbroken' : 'Connection to doHockey interrupted');
  const description = restored
    ? (nl ? 'Controleer of je laatste wijziging is opgeslagen.' : 'Check that your latest change was saved.')
    : offline
      ? (nl ? 'Controleer je internetverbinding. Opslaan kan mislukken.' : 'Check your internet connection. Saving may fail.')
      : (nl ? 'Mogelijk wordt een nieuwe versie geplaatst of is er een korte storing. Laat dit scherm open. Opslaan kan mislukken.' : 'A new version may be rolling out, or there may be a brief interruption. Keep this screen open. Saving may fail.');
  const compactDescription = restored
    ? description
    : offline
      ? (nl ? 'Controleer je internet. Opslaan kan mislukken. We verbinden automatisch opnieuw.' : 'Check your internet. Saving may fail. Reconnecting automatically.')
      : (nl ? 'Opslaan kan mislukken. We verbinden automatisch opnieuw.' : 'Saving may fail. Reconnecting automatically.');
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className={`flex shrink-0 items-start gap-2 border-y border-l-4 px-4 py-2 sm:gap-3 sm:px-6 sm:py-3 ${restored ? 'border-emerald-200 border-l-emerald-500 bg-emerald-50' : 'border-orange-200 border-l-orange-500 bg-orange-50'}`}>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded sm:h-8 sm:w-8 sm:rounded-md ${restored ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
        <Icon aria-hidden="true" size={20} className="h-4 w-4 sm:h-5 sm:w-5" />
      </span>
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-0.5 sm:gap-y-1 min-[1180px]:grid-cols-[minmax(0,1fr)_auto]">
        <p className="text-xs font-semibold leading-4 text-gray-900 sm:text-sm sm:font-bold sm:leading-5">{title}</p>
        <p className="text-xs leading-4 text-gray-700 sm:hidden">{compactDescription}</p>
        <p className="hidden text-sm leading-5 text-gray-700 sm:block min-[1180px]:col-span-2 min-[1180px]:row-start-2">{description}</p>
        {!restored && <p className="hidden text-xs font-semibold leading-5 text-orange-800 sm:block min-[1180px]:col-start-2 min-[1180px]:row-start-1">{nl ? 'We verbinden automatisch opnieuw' : 'Reconnecting automatically'}</p>}
      </div>
    </div>
  );
};

export const ConnectionStatusBanner = ({ connected, stopping, locale }: { connected: boolean; stopping: boolean; locale: string }) => {
  const [state, setState] = useState<ConnectionNotice>('hidden');
  const connection = useRef(connected);
  const serverStopping = useRef(stopping);
  const notice = useRef<ReturnType<typeof createConnectionNotice>>();
  useEffect(() => {
    const controller = createConnectionNotice(setState);
    notice.current = controller;
    const update = () => controller.update(connection.current, navigator.onLine !== false, serverStopping.current);
    const requestFailed = () => controller.update(false, navigator.onLine !== false, serverStopping.current, true);
    window.addEventListener('offline', update);
    window.addEventListener('online', update);
    window.addEventListener(API_CONNECTION_FAILED, requestFailed);
    update();
    return () => {
      controller.close();
      window.removeEventListener('offline', update);
      window.removeEventListener('online', update);
      window.removeEventListener(API_CONNECTION_FAILED, requestFailed);
    };
  }, []);
  useEffect(() => {
    connection.current = connected;
    serverStopping.current = stopping;
    notice.current?.update(connected, navigator.onLine !== false, stopping);
  }, [connected, stopping]);
  return <ConnectionStatusMessage state={state} locale={locale} />;
};
